import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import * as paymentsApi from '../api/payments';
import DonateForm from '../components/DonateForm';
import { ui, cn } from '../lib/ui';

/**
 * Public donate page for an organization: /o/:orgSlug/donate
 * (docs/Decisions.md D44). Anyone can donate unless the org requires sign-in;
 * a signed-in donor's gift is linked to their account for an in-app receipt.
 */
function Shell({ org, orgSlug, children }) {
  return (
    <div data-zone="member" className="min-h-screen bg-z-canvas">
      <header className="bg-dcon-deep text-dcon-on-deep">
        <div className={cn(ui.container, 'flex items-center gap-3 py-4')}>
          {org?.logoUrl && (
            <img
              src={org.logoUrl}
              alt={`${org.name} logo`}
              className="size-9 rounded-dcon-sm object-contain"
            />
          )}
          <Link
            to={`/o/${orgSlug}`}
            className="font-display font-semibold text-dcon-on-deep no-underline hover:no-underline"
          >
            {org?.name || 'Organization'}
          </Link>
        </div>
      </header>
      <main className={cn(ui.container, ui.page, 'max-w-2xl!')}>{children}</main>
    </div>
  );
}

export default function Donate() {
  const { orgSlug } = useParams();
  const { isAuthenticated, user } = useAuth();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const runIdRef = useRef(0);

  const load = useCallback(async () => {
    const id = ++runIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const data = await paymentsApi.getDonatePage(orgSlug);
      if (id === runIdRef.current) setPage(data);
    } catch (err) {
      if (id === runIdRef.current) setError(err.message || 'Organization not found');
    } finally {
      if (id === runIdRef.current) setLoading(false);
    }
  }, [orgSlug]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Shell orgSlug={orgSlug}>
        <p className={ui.muted} role="status">
          Loading...
        </p>
      </Shell>
    );
  }

  if (error || !page) {
    return (
      <Shell orgSlug={orgSlug}>
        <div className={ui.alertError} role="alert">
          {error || 'Organization not found'}
        </div>
      </Shell>
    );
  }

  const org = page.org;
  return (
    <Shell org={org} orgSlug={orgSlug}>
      <h1 className={ui.h1}>Donate to {org.name}</h1>
      {!page.available ? (
        <div className={ui.alertInfo} role="status">
          {org.name} is not accepting donations online right now.
        </div>
      ) : !isAuthenticated && page.requireSignIn !== false ? (
        <div className={ui.cardFlat}>
          <p className="mb-4 text-body">
            Sign in to your account to donate to {org.name}. Every donation is made from a
            registered account.
          </p>
          <div className={ui.row}>
            <Link
              to={`/o/${orgSlug}/login`}
              state={{ from: `/o/${orgSlug}/donate` }}
              className={ui.btnPrimary}
            >
              Sign in
            </Link>
            <Link to={`/o/${orgSlug}/register`} className={ui.btnSecondary}>
              Create an account
            </Link>
          </div>
        </div>
      ) : (
        <div className={ui.card}>
          <p className={cn(ui.muted, 'mb-4 text-body-s')}>
            {isAuthenticated
              ? `Donating as ${user?.email || 'your account'}.`
              : 'Donating as a guest. Stripe asks for your email for the receipt.'}{' '}
            All amounts are in US dollars (USD).
          </p>
          <DonateForm page={page} orgSlug={orgSlug} signedIn={isAuthenticated} />
        </div>
      )}
      {page.available && page.disclosure && <NonprofitDisclosure disclosure={page.disclosure} />}
    </Shell>
  );
}

/**
 * Who receives the gift and what the donor may rely on (docs/Decisions.md
 * D64), from the nonprofit's verified profile. Shown before the donor gives,
 * and repeated on the receipt.
 */
function NonprofitDisclosure({ disclosure }) {
  return (
    <section className={cn(ui.cardFlat, 'mt-4 text-body-s')} aria-labelledby="donor-disclosure">
      <h2 id="donor-disclosure" className="mt-0 mb-2 text-title-s">
        About this nonprofit
      </h2>
      <p className="mb-1">
        Your gift is received by <strong>{disclosure.legalName}</strong> (EIN {disclosure.ein}).
      </p>
      <p className="mb-1">{disclosure.statement}</p>
      {disclosure.caRegistryNumber && (
        <p className="m-0">
          California Registry of Charities and Fundraisers number {disclosure.caRegistryNumber}.
        </p>
      )}
    </section>
  );
}

/** /o/:orgSlug/donate/success and /o/:orgSlug/donate/cancel */
export function DonateResult({ status }) {
  const { orgSlug } = useParams();
  const success = status === 'success';
  return (
    <Shell orgSlug={orgSlug}>
      <h1 className={ui.h1}>{success ? 'Thank you for your donation' : 'Donation cancelled'}</h1>
      <div className={success ? ui.alertSuccess : ui.alertInfo} role="status">
        {success
          ? 'Your payment was submitted. Stripe emails a receipt, and bank payments can take a few business days to clear.'
          : 'You were not charged. You can start again whenever you are ready.'}
      </div>
      <div className={ui.row}>
        <Link to={`/o/${orgSlug}/donate`} className={success ? ui.btnSecondary : ui.btnAccent}>
          {success ? 'Make another donation' : 'Return to donate'}
        </Link>
        <Link to={`/o/${orgSlug}`} className={ui.btnGhost}>
          Back to the organization
        </Link>
      </div>
    </Shell>
  );
}
